/**
 * Restaurant Page — Main Entry Point
 * Exports renderRestaurantPage() + initRestaurantPage() for main.ts (unchanged interface).
 *
 * Modes:
 *   'order'   → MenuView (filter chips + category tabs + menu sections + cart bar)
 *   'reserve' → ReservationView (existing table booking form)
 *
 * Mode Toggle is rendered BEFORE the meal period card (per AGENTS.md rule).
 */

import { Header, renderServiceTabs } from '../../components';
import { getCurrentLanguage, t } from '../../i18n';
import { cleanupMenuView, initMenuView, renderMenuView } from './menu-view';
import { initReservationHandlers, renderReservationBody, getActiveOutlet } from './reservation-view';
import { renderModeToggle } from './components/mode-toggle';
import type { RestaurantMode } from './components/mode-toggle';

// ── Module-level: which mode is active ─────────────────────────────────────
// Default to 'reserve' (เข้าหน้าร้านอาหารปกติเพื่อจองโต๊ะ หรือสลับสั่งอาหาร)
function resolveInitialMode(): RestaurantMode {
  if (typeof window !== 'undefined' && window.location) {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') === 'order' || params.has('table')) return 'order';
  }
  return 'reserve';
}

let restaurantMode: RestaurantMode = resolveInitialMode();

// ── Render ──────────────────────────────────────────────────────────────────
export function renderRestaurantPage(): string {
  const lang = getCurrentLanguage();
  const activeOutlet = getActiveOutlet();
  const restNameText = (() => {
    const n = activeOutlet?.name;
    if (!n) return t('appName');
    if (typeof n === 'string') return n;
    return (n as unknown as Record<string, string>)[lang] || (n as any).en || (n as any).th || t('appName');
  })();

  const modeSwitcher = renderModeToggle(restaurantMode);
  const menuHtml = restaurantMode === 'order' ? renderMenuView() : '';
  const pageContent = renderReservationBody(modeSwitcher, restaurantMode, menuHtml);

  return `
    ${Header(restNameText, false, true)}
    ${renderServiceTabs('restaurant')}
    <div class="page-content" id="restaurant-content" style="padding: 16px; padding-bottom: 100px;">
      ${pageContent}
    </div>
  `;
}

// ── Init ─────────────────────────────────────────────────────────────────────
export function initRestaurantPage(): void {
  // Handle mode switch button
  (window as any).setRestaurantMode = (mode: RestaurantMode) => {
    if (restaurantMode === mode) return;
    restaurantMode = mode;
    cleanupMenuView();

    // Re-render whole restaurant page smoothly preserving scroll or via renderApp
    const savedY = window.scrollY;
    (window as any).renderApp?.();
    window.scrollTo({ top: savedY, behavior: 'instant' as ScrollBehavior });
  };

  // Init handlers for the currently active mode
  const activeOutlet = getActiveOutlet();
  if (restaurantMode === 'order') {
    initReservationHandlers();
    initMenuView(activeOutlet?.id);
  } else {
    initReservationHandlers();
  }
}



